import React, { useEffect, useState } from "react";
import { Card, Select, Timeline, Tag, Empty, Table } from "antd";
import { useLocation } from "react-router-dom";
import { formatTimeInTz, formatFullInTz, tzLabel } from "../../helper/timezone";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageHeader from "../../components/ui/PageHeader";
import PageTransition from "../../components/ui/PageTransition";
import EmptyState from "../../components/ui/EmptyState";
import StatusTag from "../../components/ui/StatusTag";

const eventColor = {
  login: "green",
  location_check: "blue",
  geofence_exit: "orange",
  geofence_entry: "green",
  auto_logout: "red",
  manual_logout: "gray",
  wfh_start: "purple",
  wfh_end: "purple",
  login_rejected: "red",
};

const ActivityHistory = () => {
  const { request } = useRequest();
  const location = useLocation();
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState(location.state?.employeeId);
  const [logs, setLogs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [tz, setTz] = useState(null); // the employee's office timezone

  useEffect(() => {
    request({ url: apiPath.employees, method: "GET", onSuccess: (res) => setEmployees(res?.data?.employees || []) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchHistory = (id) => {
    if (!id) return;
    request({
      url: apiPath.employeeActivityHistory(id),
      method: "GET",
      onSuccess: (res) => {
        setLogs(res?.data?.logs || []);
        setSessions(res?.data?.sessions || []);
        setTz(res?.data?.timezone || null);
      },
    });
  };

  useEffect(() => {
    if (employeeId) fetchHistory(employeeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const sessionColumns = [
    { title: "Date", dataIndex: "date" },
    { title: "Status", dataIndex: "status", render: (s) => <StatusTag status={s} /> },
    { title: "Shift Start", dataIndex: "loginAt", render: (v) => formatTimeInTz(v, tz) },
    { title: "Shift End", dataIndex: "logoutAt", render: (v) => formatTimeInTz(v, tz) },
    { title: "Ended By", dataIndex: "logoutType" },
    {
      title: "Activity",
      dataIndex: "presence",
      render: (p) => (p ? <Tag color={p === "active" ? "green" : "orange"}>{p === "active" ? "Active" : "Inactive"}</Tag> : "-"),
    },
  ];

  return (
    <PageTransition>
      <PageHeader title="Activity History" subtitle="Inspect an employee's session and geo-activity history" />
      <Card title="Select Employee" style={{ marginBottom: 16 }}>
        <Select
          showSearch
          placeholder="Select employee"
          style={{ width: 320 }}
          value={employeeId}
          onChange={setEmployeeId}
          filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
          options={employees.map((e) => ({ value: e._id, label: `${e.name} (${e.employeeId})` }))}
        />
      </Card>

      {employeeId ? (
        <>
          <Card title="Recent Sessions" style={{ marginBottom: 16 }}>
            <Table
              rowKey="_id"
              columns={sessionColumns}
              dataSource={sessions}
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: <EmptyState message="No sessions recorded" /> }}
            />
          </Card>
          <Card title="Geo Activity Timeline">
            {logs.length ? (
              <Timeline mode="left">
                {logs.map((log) => (
                  <Timeline.Item key={log._id} color={eventColor[log.eventType] || "gray"}>
                    <b>{log.eventType}</b> — {formatFullInTz(log.timestamp, tz)}{tz ? ` (${tzLabel(tz)})` : ""}
                    <br />
                    {log.lat && log.lng ? `lat: ${log.lat.toFixed(5)}, lng: ${log.lng.toFixed(5)}` : ""}
                    {log.distanceMeters != null ? ` — ${Math.round(log.distanceMeters)}m from office` : ""}
                    {log.geofenceStatus ? <Tag style={{ marginLeft: 8 }} color={log.geofenceStatus === "inside" ? "green" : "red"}>{log.geofenceStatus}</Tag> : null}
                    {log.accuracy != null ? (
                      <Tag
                        style={{ marginLeft: 4 }}
                        color={log.accuracy > 1000 ? "orange" : "default"}
                        title="Device-reported location accuracy radius — GPS is typically 5-20m, desktop/WiFi-based positioning can be hundreds of meters to tens of km"
                      >
                        ±{log.accuracy > 1000 ? `${(log.accuracy / 1000).toFixed(1)}km` : `${Math.round(log.accuracy)}m`} accuracy
                      </Tag>
                    ) : null}
                    {log.note ? <div style={{ color: "#888" }}>{log.note}</div> : null}
                  </Timeline.Item>
                ))}
              </Timeline>
            ) : (
              <Empty description="No activity logged yet" />
            )}
          </Card>
        </>
      ) : (
        <Card>
          <Empty description="Select an employee to view their activity history" />
        </Card>
      )}
    </PageTransition>
  );
};

export default ActivityHistory;
